import {Button, Pagination, Tooltip, Select, Spin} from 'antd';
import {Table, Switch, Space} from 'antd';
import {LoadingOutlined} from "@ant-design/icons";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {v4} from "uuid";
import {getGridTotal, useDataGrid} from "./DataGridContext";
import {useTranslation} from "react-i18next";
import {EyeOutlined, ReloadOutlined, UnorderedListOutlined} from "@ant-design/icons";
import {useModal} from "../../context/ModalContext";
import ColumnFilter from "../ColumnFilter";
import MobileCardList from "./MobileCardList";
import { useIsSmallScreen } from '../../utils/useMediaQuery';
import { createVirtualBody } from "./VirtualBody";
import { useClipboard } from "../../contexts/ClipboardContext";
import "./data-grid.css";


const SORT = {
    NONE: -1,
    ASC: 0,
    DESC: 1
}

// Fallback width for columns that don't declare one, used only in virtual mode
// (fixed layout requires a fully-sized colgroup for header/body to align).
const DEFAULT_VIRTUAL_COL_WIDTH = 150;

// Sayfa boyutu seçenekleri. Üst sınır 500'dür ve sunucu da aynı sınırı uygular
// (RepositoryExtension.MaxPageSize) — "Tümü" seçeneği kaldırıldı.
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 250, 500];

// Sanal grid gövdesinin (scroll.y) yüksekliği. Sabit kabuk (header + filtre satırı +
// araç çubuğu + sayfalama) için ~300px pay bırakılır. Ayrıca alt bant (footer) SABİT
// konumlu ve içerik alanı ondan `--footer-h + 2px` kadar dolgu ayırıyor; footer
// yüksekliğini de düşmezsek sayfa tam footer boyu kadar taşıp dikey scroll çıkıyor.
const GRID_CHROME_OFFSET = 300;

// Tablo içi yükleme göstergesi. Modül düzeyinde sabit: her render'da yeni nesne üretmek
// antd Table'ın spinProps useMemo'sunu boşa düşürürdü.
const TABLE_LOADING = {indicator: <Spin indicator={<LoadingOutlined spin/>} size={'large'}/>};
export function getGridBodyHeight() {
    let footer = 50; // --footer-h (48) + amber üst çizgi (2); CSS okunamazsa yedek
    if (typeof document !== 'undefined') {
        const px = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--footer-h'),
            10
        );
        if (Number.isFinite(px)) footer = px + 2;
    }
    return window.innerHeight - GRID_CHROME_OFFSET - footer;
}

export const RenderableExpandRow = ({children, expanded, recordId}) => {

    const {t} = useTranslation();
    const {renderItem, renderExpandedRow, refreshDataGrid} = useDataGrid()
    const [item, setItem] = useState('');

    useEffect(() => {
        if (renderItem && renderItem.recordId === recordId) {
            setItem(renderItem)
        }
    }, [renderItem])

    return (
        <>
            {
                expanded && (
                    <div key={item.key}>
                        {/*<Tooltip placement={'bottomLeft'} title={t('Refresh')}>*/}
                        {/*    <Button onClick={() => {*/}
                        {/*        renderExpandedRow(recordId)*/}
                        {/*    }}>*/}
                        {/*        <ReloadOutlined/>*/}
                        {/*    </Button>*/}
                        {/*</Tooltip>*/}
                        {children}
                    </div>
                )
            }
        </>
    )
}

// Kolon sırası tarayıcıda, gizle/göster kaydından AYRI anahtarda saklanır (eski kayıtlar bozulmaz).
// Yalnız kolon anahtarları tutulur; başlık/render her zaman koddaki tanımdan gelir.
const columnOrderStorageKey = (name) => `data-grid-${name}-order`;

function readColumnOrder(name) {
    try {
        const order = JSON.parse(localStorage.getItem(columnOrderStorageKey(name)) || 'null');
        return Array.isArray(order) ? order.map(String) : null;
    } catch {
        return null;
    }
}

function writeColumnOrder(name, order) {
    try {
        if (order)
            localStorage.setItem(columnOrderStorageKey(name), JSON.stringify(order));
        else
            localStorage.removeItem(columnOrderStorageKey(name));
    } catch {
        // Depolama kapalı/dolu: sıra yalnız bu sayfa açıkken geçerli kalır.
    }
}

const isSortItemOfColumn = (item, col) =>
    item.name === col.columnName || item.name === col.key || item.name === col.dataIndex;

// Başlık oku sorgudaki sıralamadan türetilir; ok ile gelen sonuç asla ayrışmaz.
function columnSortOrder(col, items) {
    const item = (items || []).find(o => isSortItemOfColumn(o, col));
    return item ? (item.operation === SORT.ASC ? 'ascend' : 'descend') : null;
}

// Teknik kolonlar (`__` önekli, ör. detay "+") ve sabitlenmiş kolonlar yerinde kalır.
const isReorderableColumn = (col) => col.key != null && !col.fixed && !String(col.key).startsWith('__');

/**
 * Kayıtlı sırayı koddaki kolon listesiyle birleştirip TAM anahtar sırasını döner.
 * Kayıtta olup artık kodda olmayan anahtar atılır; koda sonradan eklenen kolon, varsayılan
 * sırada kendinden önce gelen kolonun hemen arkasına yerleşir (sona düşüp gözden kaybolmaz).
 */
function resolveColumnOrder(allColumns, savedOrder) {
    const keys = allColumns.map(c => String(c.key));
    if (!savedOrder?.length)
        return keys;

    const known = new Set(keys);
    const order = savedOrder.filter((k, i) => known.has(k) && savedOrder.indexOf(k) === i);
    keys.forEach((key, i) => {
        if (order.includes(key))
            return;
        const prev = keys.slice(0, i).reverse().find(k => order.includes(k));
        order.splice(prev === undefined ? 0 : order.indexOf(prev) + 1, 0, key);
    });
    return order;
}

function sortColumnsByOrder(cols, order) {
    const index = new Map(order.map((k, i) => [k, i]));
    return [...cols].sort((a, b) => (index.get(String(a.key)) ?? 0) - (index.get(String(b.key)) ?? 0));
}

// Fare bu kadar piksel kaymadan sürükleme başlamaz: başlığa normal tıklama sıralamaya gitsin.
const COLUMN_DRAG_THRESHOLD = 5;

// Sağ tıkın tarayıcı menüsüne bırakıldığı hedefler: kendi davranışı olan kontroller.
const CELL_COPY_IGNORE_TARGET = 'a, button, input, textarea, select, [role="button"], .ant-select, .ant-checkbox, .ant-radio, .ant-switch, .ant-picker';

/**
 * Sağ tıklanan veri hücresinin GÖRÜNEN metni (tarih biçimli, enum çevrilmiş — kullanıcı ne
 * görüyorsa o); kopyalanacak bir şey yoksa ya da tarayıcı menüsü açılmalıysa null.
 * - Shift basılı: her zaman tarayıcı menüsü (İncele vb. erişilebilir kalsın).
 * - Olay zaten işlenmiş (RowMenu butonu kendi değerini kopyalar): ikinci kez kopyalama.
 * - Hücrede metin seçiliyse: tarayıcının "Kopyala"sı kısmi seçimi bozmadan çalışsın.
 * - Yalnız veri satırları (`tr[data-row-key]`): başlık, genişletilmiş satır, sanal boşluk
 *   satırları ve seçim/genişletme kolonları hariç.
 */
function getCopyableCellText(e) {
    if (e.defaultPrevented || e.shiftKey)
        return null;

    const target = e.target;
    if (!target || typeof target.closest !== 'function' || target.closest(CELL_COPY_IGNORE_TARGET))
        return null;

    const td = target.closest('td');
    if (!td || !td.parentElement?.matches('tr[data-row-key]:not(.ant-table-expanded-row)'))
        return null;
    if (td.classList.contains('ant-table-selection-column') || td.classList.contains('ant-table-row-expand-icon-cell'))
        return null;

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.toString().trim() && selection.containsNode(td, true))
        return null;

    const text = (td.innerText ?? '').replace(/\s+/g, ' ').trim();
    return text || null;
}

export default function DataGrid({title, name, mobileCardTitle}) {
    const {t} = useTranslation();
    const {
        resultModel,
        dataGridProps,
        setSelectedRows,
        onFilterPageSort,
        tableId,
        selectedRows,
        queryModel,
        focusOut,
        refreshDataGrid,
        count,
        loadExactCount,
        loading,
        sortStorageKey
    } = useDataGrid()
    const [columns, setColumns] = useState([])
    const {openModal, closeModal} = useModal()
    const {addText} = useClipboard();
    const isMobile = useIsSmallScreen();

    const rowKey = dataGridProps.rowKey || 'id';
    const virtual = !!dataGridProps.virtual;

    // Measured content width of the virtual table body, used to scale columns to
    // fill the screen.
    const tableWrapRef = useRef(null);
    const [availableWidth, setAvailableWidth] = useState(0);

    useEffect(() => {
        if (!virtual) return;
        const wrap = tableWrapRef.current;
        if (!wrap || typeof ResizeObserver === 'undefined') return;

        const measure = () => {
            const body = wrap.querySelector('.ant-table-body');
            const w = body ? body.clientWidth : wrap.clientWidth;
            if (w) setAvailableWidth(prev => (prev === w ? prev : w));
        };

        // Yeni genişlik BOYAMADAN ÖNCE uygulanmalı. Kolon genişlikleri (ve scroll.x)
        // bu ölçümden türetiliyor; ölçümü bir sonraki kareye bırakırsak o kare boyunca
        // tablo eski/geniş kolonlarla daralmış kapsayıcıya çizilir, sığmadığı için
        // .ant-table-body'de yatay scrollbar çıkar ve hemen kaybolur → filtre panelini
        // açıp kapatırken flicker. ResizeObserver layout'tan sonra, paint'ten önce
        // çalışıyor; flushSync ile render'ı aynı karede bitirip ara kareyi yok ediyoruz.
        // (Effect içindeki ilk çağrı flushSync'siz: lifecycle içinde flushSync uyarı verir.)
        const measureBeforePaint = () => flushSync(measure);

        measure();
        // Pencere yeniden boyutlandırmada wrap'ın genişliği de değiştiği için ayrı bir
        // window 'resize' dinleyicisi gerekmez; olurdu ise aynı karede ikinci bir
        // senkron render'a yol açardı.
        const ro = new ResizeObserver(measureBeforePaint);
        ro.observe(wrap);
        return () => ro.disconnect();
    }, [virtual, resultModel.data]);

    // Latest values accessible from the (stable) rowSelection callbacks without
    // rebuilding them.
    const setSelectedRowsRef = useRef(setSelectedRows);
    setSelectedRowsRef.current = setSelectedRows;
    const dataRef = useRef(resultModel.data);
    dataRef.current = resultModel.data;
    const selectedRowsRef = useRef(selectedRows);
    selectedRowsRef.current = selectedRows;
    // İsteğe bağlı: bazı satırların checkbox'ı pasif (antd getCheckboxProps). Shift-aralık seçimi de atlar.
    const getCheckboxPropsRef = useRef(dataGridProps.getCheckboxProps);
    getCheckboxPropsRef.current = dataGridProps.getCheckboxProps;

    // Shift-range selection: anchor = last plainly (un-shifted) toggled row;
    // shiftActionRef carries the pending range from onSelect to onChange.
    const anchorIndexRef = useRef(null);
    const shiftActionRef = useRef(null);

    // Derive selected keys from the single source of truth (context.selectedRows)
    // instead of a second state copy + sync effect, which double-rendered the
    // whole table on every checkbox click.
    const selectedRowKeys = useMemo(
        () => selectedRows.map(x => x[rowKey]),
        [selectedRows, rowKey]
    );

    // Fires before onChange; record whether this click is a shift-range and set
    // the anchor otherwise.
    const handleSelect = useCallback((record, selected, _rows, nativeEvent) => {
        const data = dataRef.current || [];
        const idx = data.findIndex(r => r[rowKey] === record[rowKey]);
        if (nativeEvent?.shiftKey && anchorIndexRef.current != null && idx !== -1) {
            shiftActionRef.current = { from: anchorIndexRef.current, to: idx, select: selected };
        } else {
            shiftActionRef.current = null;
            anchorIndexRef.current = idx;
        }
    }, [rowKey]);

    const handleSelectAll = useCallback(() => {
        shiftActionRef.current = null;
        anchorIndexRef.current = null;
    }, []);

    const handleSelectionChange = useCallback((_selectedRowKeys, rows) => {
        const action = shiftActionRef.current;
        shiftActionRef.current = null;

        if (action) {
            // Apply the range [from..to] on top of the existing selection.
            const data = dataRef.current || [];
            const [lo, hi] = action.from <= action.to ? [action.from, action.to] : [action.to, action.from];
            const keySet = new Set((selectedRowsRef.current || []).map(r => r[rowKey]));
            for (let i = lo; i <= hi; i++) {
                const r = data[i];
                if (!r) continue;
                if (getCheckboxPropsRef.current?.(r)?.disabled) continue;
                if (action.select) keySet.add(r[rowKey]);
                else keySet.delete(r[rowKey]);
            }
            setSelectedRowsRef.current(data.filter(r => keySet.has(r[rowKey])));
            anchorIndexRef.current = action.to;
            return;
        }

        setSelectedRowsRef.current(rows);
    }, [rowKey]);

    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: handleSelectionChange,
        onSelect: handleSelect,
        onSelectAll: handleSelectAll,
        getCheckboxProps: dataGridProps.getCheckboxProps,
        // Explicit width so the colgroup is fully specified. With a fixed table
        // layout + virtualization, an unsized selection column makes the header
        // and body compute its width differently and misaligns the columns.
        columnWidth: 40,
    }), [selectedRowKeys, handleSelectionChange, handleSelect, handleSelectAll, dataGridProps.getCheckboxProps]);

    // Reset the shift-selection anchor whenever the data changes (its row indices
    // would otherwise be stale).
    useEffect(() => {
        anchorIndexRef.current = null;
        shiftActionRef.current = null;
    }, [resultModel.data]);

    // Row virtualization: only mount the visible rows. Keeps native rows so row
    // colors / selection / onRow are unaffected. Requires scroll.y (set by the
    // caller's antdTableProps).
    const virtualComponents = useMemo(
        () => virtual
            ? {body: {wrapper: createVirtualBody(dataGridProps.virtualRowHeight || 39)}}
            : undefined,
        [virtual, dataGridProps.virtualRowHeight]
    );

    useEffect(() => {
        if (dataGridProps.columns) {
            const selectedCols = JSON.parse(localStorage.getItem(`data-grid-${name}`) || '[]')
            const order = resolveColumnOrder(dataGridProps.columns, readColumnOrder(name))
            setColumns(
                sortColumnsByOrder(
                    dataGridProps.columns
                        .filter(x => {
                            return selectedCols.find(y => y.key === x.key) || selectedCols.length === 0
                        })
                        .map(x => {
                            // Kolonlar sonradan gelir; saklanıp geri yüklenen sıralamanın oku da görünsün.
                            x.sortOrder = columnSortOrder(x, queryModel.orderByItems)
                            return x
                        }),
                    order
                )
            )
        }
    }, [dataGridProps.columns])

    // Saklanan sıralama artık kodda olmayan bir kolonu gösteriyorsa (kolon kaldırıldı / adı değişti)
    // sunucu o alanla sıralayamaz; grid her açılışta hata vermesin diye geçersiz öğeler atılır.
    useEffect(() => {
        const cols = dataGridProps.columns
        const items = queryModel.orderByItems || []
        if (!sortStorageKey || !cols?.length || !items.length)
            return
        const valid = items.filter(o => cols.some(x => isSortItemOfColumn(o, x)))
        if (valid.length !== items.length)
            onFilterPageSort({orderByItems: valid})
    }, [dataGridProps.columns])

    // Başlığı sürükle-bırak ile kolon sıralama. Tarayıcının yerel (HTML5 draggable) sürükle-bırakı
    // KULLANILMAZ: o oturum bitmeden takılıp sayfadaki bütün tıklamaları kilitliyordu. Sürükleme,
    // çekmecenin genişlik kolu gibi mousedown/mousemove/mouseup ile elle izlenir.
    // Sürükleme boyunca React state'i DEĞİŞMEZ: işaretler doğrudan th'nin class'ında tutulur;
    // state'e bağlansaydı her fare hareketinde sanal tablonun tamamı yeniden render olurdu.
    // State yalnız bırakınca (tek sefer) güncellenir.
    const allColumnsRef = useRef(dataGridProps.columns);
    allColumnsRef.current = dataGridProps.columns;
    // Süren sürüklemeyi iptal eden fonksiyon; grid sürükleme ortasında kapanırsa dinleyiciler kalmasın.
    const cancelColumnDragRef = useRef(null);
    useEffect(() => () => cancelColumnDragRef.current?.(), []);

    const startColumnDrag = useCallback((e, fromKey) => {
        if (e.button !== 0 || cancelColumnDragRef.current)
            return;
        const source = e.currentTarget;
        // Başlıktaki bir portal (filtre paneli vb.) içinden gelen olay React ağacında buraya da ulaşır;
        // başlık içindeki etkileşimli öğeler (filtre ikonu, düğme, input) de sürüklemeyi başlatmaz.
        if (!source.contains(e.target) || e.target.closest('input, textarea, button, .ant-table-filter-trigger'))
            return;

        const grid = source.closest('.app-data-grid');
        const startX = e.clientX;
        const startY = e.clientY;
        let started = false;
        let ghost = null;
        let target = null;
        let side = null;

        const clearMark = () => {
            target?.classList.remove('data-grid-th-drop-before', 'data-grid-th-drop-after');
            target = null;
            side = null;
        };

        const onMove = (ev) => {
            if (!started) {
                if (Math.abs(ev.clientX - startX) < COLUMN_DRAG_THRESHOLD && Math.abs(ev.clientY - startY) < COLUMN_DRAG_THRESHOLD)
                    return;
                started = true;
                window.getSelection()?.removeAllRanges();
                source.classList.add('data-grid-th-dragging');
                document.body.classList.add('data-grid-column-dragging');
                ghost = document.createElement('div');
                ghost.className = 'data-grid-column-ghost';
                ghost.textContent = source.textContent;
                document.body.appendChild(ghost);
            }
            ev.preventDefault();
            ghost.style.transform = `translate(${ev.clientX + 12}px, ${ev.clientY + 12}px)`;

            // Hedef yalnız AYNI grid'in başlığı olabilir (iç içe grid'ler kendi sıralarını tutar).
            // Hayalet etiket pointer-events: none olduğu için elementFromPoint onu atlar.
            const th = document.elementFromPoint(ev.clientX, ev.clientY)?.closest('th[data-column-key]');
            const over = th && th !== source && th.closest('.app-data-grid') === grid ? th : null;
            if (!over) {
                clearMark();
                return;
            }
            const rect = over.getBoundingClientRect();
            const nextSide = ev.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
            if (over === target && nextSide === side)
                return;
            clearMark();
            target = over;
            side = nextSide;
            target.classList.add(`data-grid-th-drop-${side}`);
        };

        const end = (commit) => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('blur', onCancel);
            cancelColumnDragRef.current = null;
            if (!started)
                return; // Sürükleme hiç başlamadı: normal tıklama, sıralama çalışsın.

            const toKey = target?.getAttribute('data-column-key');
            const dropSide = side;
            clearMark();
            source.classList.remove('data-grid-th-dragging');
            document.body.classList.remove('data-grid-column-dragging');
            ghost?.remove();

            if (!commit)
                return;

            // Bırakınca tarayıcı bir click üretir; başlığın sıralamasını tetiklemesin.
            const swallowClick = (ce) => {
                ce.stopPropagation();
                ce.preventDefault();
            };
            window.addEventListener('click', swallowClick, true);
            setTimeout(() => window.removeEventListener('click', swallowClick, true), 0);

            if (!toKey || toKey === fromKey)
                return;

            // Sıra gizli kolonları da kapsar: gizlenip sonra açılan kolon kaldığı yere döner.
            const order = resolveColumnOrder(allColumnsRef.current || [], readColumnOrder(name))
                .filter(k => k !== fromKey);
            const index = order.indexOf(toKey);
            if (index === -1)
                return;
            order.splice(dropSide === 'after' ? index + 1 : index, 0, fromKey);

            writeColumnOrder(name, order);
            setColumns(cols => sortColumnsByOrder(cols, order));
        };

        const onUp = () => end(true);
        const onCancel = () => end(false);
        const onKeyDown = (ev) => {
            if (ev.key !== 'Escape' || !started)
                return;
            ev.preventDefault();
            ev.stopPropagation();
            end(false);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('blur', onCancel);
        cancelColumnDragRef.current = onCancel;
    }, [name]);

    // Keep the header sort arrows in sync with the ACTUAL query order on every
    // query change (filter/sort/refresh). Previously this depended on
    // `queryModel.id` — a field the reducer never sets (it sets `updateKey`) — so
    // it was dead code and the arrows could linger after the sort was reset,
    // making the table look sorted while the results were not. Deriving the arrow
    // straight from `orderByItems` makes the two impossible to disagree.
    useEffect(() => {
        const items = queryModel.orderByItems || []
        setColumns(cols => {
            let changed = false
            cols.forEach(x => {
                const sortOrder = columnSortOrder(x, items)
                if (x.sortOrder !== sortOrder) {
                    x.sortOrder = sortOrder
                    changed = true
                }
            })
            return changed ? [...cols] : cols
        })
    }, [queryModel.updateKey])

    // Study/Report/HL7'de sayım ayrı istekle gelir (grid cevabı totalRecords = -1); diğer grid'lerde
    // eskisi gibi grid cevabındadır. Farkı getGridTotal soğurur.
    const {total: gridTotal, deferred: deferredCount, capped, known} = getGridTotal(resultModel, count);

    // Kaç satır seçili olduğu toplamın yanında gösterilir: toplu işlem yapmadan önce
    // kullanıcı kaç kayda dokunacağını görsün. Sayfa/filtre değişince seçim sıfırlandığı
    // için (bkz. ON_FILTER_PAGE_SORT) bu sayı daima açık sayfaya aittir.
    // Ek render maliyeti yok: DataGrid zaten selectedRows'a abone (selectedRowKeys/rowSelection)
    // ve her seçim değişiminde yeniden render oluyor.
    const selectedCount = dataGridProps.enableRowSelection ? selectedRows.length : 0;

    const renderTotalText = (total) => {
        if (!deferredCount)
            return `Toplam ${total} kayıt`;

        // Sayım henüz gelmedi: küresel gösterge yerine burada — "gerçek sayıyı getir"
        // butonunun çıkacağı yerde — küçük bir yerel spinner göster.
        if (!known)
            return (
                <span>
                    {'Toplam '}
                    <Spin indicator={<LoadingOutlined spin/>} size="small" style={{margin: '0 4px'}}/>
                    {' kayıt'}
                </span>
            );

        // Tavana değdiyse gerçek sayı daha büyüktür: "500+" ve kesin sayımı isteme seçeneği.
        if (capped) {
            return (
                <span>
                    {`Toplam ${count.value}+ kayıt`}
                    <Button className={'btn-accent'} size={'small'} style={{marginLeft: 8}} loading={count.loading} onClick={loadExactCount}>
                        {t('getExactCount')}
                    </Button>
                </span>
            );
        }

        return `Toplam ${count.value} kayıt`;
    };

    const renderTotal = (total) => (
        <>
            {renderTotalText(total)}
            {selectedCount > 0 && (
                <span style={{marginInlineStart: 8, color: 'var(--accent-dark)', fontWeight: 600}}>
                    {t('gridSelectedRowCount', {count: selectedCount})}
                </span>
            )}
        </>
    );

    const getSortType = (sort) => {
        if (!sort)
            return SORT.NONE;
        else if (sort === 'ascend')
            return SORT.ASC
        else
            return SORT.DESC
    }

    const onChange = (pagination, filters, sorter) => {
        const _columns = [...columns].map(x => {
            x.sortOrder = ''
            return x;
        })

        const orderByItems = []
        const col = _columns.find(x => x.key === sorter.field);
        if (sorter.order && col) {
            col.sortOrder = sorter.order
            orderByItems.push({
                name: col.columnName || sorter.field,
                operation: getSortType(sorter.order)
            })
        } else {
            if(col) {
                col.sortOrder = null
            }
        }

        const paginationQuery = {
            pageNumber: pagination.current,
            pageSize: pagination.pageSize
        };

        onFilterPageSort({
            filter: queryModel.filter,
            pagination: paginationQuery,
            orderByItems
        })

        setColumns(_columns)
    }

    // Give every column a default `shouldCellUpdate` keyed on record identity.
    // antd regenerates the columns array on every selection change, which makes
    // rc-table's MemoCell fall back to shallowEqual and re-run EVERY cell's
    // render (rows × columns). With this, a selection change skips the expensive
    // cell renders and only the checkbox column updates. Data updates replace
    // records with new objects, so real changes still render.
    const tableColumns = useMemo(
        () => columns.map(col => {
            let withUpdate = col.shouldCellUpdate
                ? col
                : {...col, shouldCellUpdate: (record, prevRecord) => record !== prevRecord};
            if (isReorderableColumn(col)) {
                const key = String(col.key);
                const originHeaderCell = col.onHeaderCell;
                withUpdate = {
                    ...withUpdate,
                    onHeaderCell: (column) => {
                        const cell = originHeaderCell?.(column) || {};
                        return {
                            ...cell,
                            'data-column-key': key,
                            onMouseDown: (e) => {
                                cell.onMouseDown?.(e);
                                startColumnDrag(e, key);
                            },
                            className: [cell.className, 'data-grid-th-draggable'].filter(Boolean).join(' '),
                        };
                    },
                };
            }
            // Virtualization needs a fixed layout with EVERY column sized, else the
            // header and body compute widths differently and misalign. Give any
            // width-less column a sane default so any grid can just flip on
            // `virtual` without auditing its columns.
            if (virtual && !withUpdate.width) {
                return {...withUpdate, width: DEFAULT_VIRTUAL_COL_WIDTH};
            }
            return withUpdate;
        }),
        [columns, virtual, startColumnDrag]
    );

    // Treat the entered widths as ratios: on a screen wider than their sum, scale
    // every column up proportionally so the table fills the width (no dead gap on
    // the right). On narrower screens keep the entered widths (horizontal scroll).
    // `availableWidth` is the real content width of the body (measured below).
    const selWidth = dataGridProps.enableRowSelection ? 40 : 0;
    const scaledColumns = useMemo(() => {
        if (!virtual || !availableWidth) return tableColumns;

        const baseSum = tableColumns.reduce((s, c) => s + (c.width || DEFAULT_VIRTUAL_COL_WIDTH), 0);
        const dataTarget = availableWidth - selWidth;
        if (baseSum <= 0 || dataTarget <= baseSum) return tableColumns; // narrow → keep

        const scale = dataTarget / baseSum;
        const scaled = tableColumns.map(c => ({
            ...c,
            width: Math.floor((c.width || DEFAULT_VIRTUAL_COL_WIDTH) * scale),
        }));

        // Give the rounding leftover to the last column so it fills exactly.
        const used = scaled.reduce((s, c) => s + c.width, 0);
        const leftover = dataTarget - used;
        if (leftover > 0 && scaled.length) {
            scaled[scaled.length - 1] = {
                ...scaled[scaled.length - 1],
                width: scaled[scaled.length - 1].width + leftover,
            };
        }
        return scaled;
    }, [virtual, tableColumns, availableWidth, selWidth]);

    // In virtual mode, DataGrid owns the table sizing: a complete-width colgroup,
    // a matching scroll.x, fixed layout, and a scroll.y (from the caller or a
    // sensible default) that creates the scroll container we virtualize inside.
    const {scroll: callerScroll, tableLayout: callerLayout, ...restAntdProps} = dataGridProps.antdTableProps || {};
    const mergedScroll = useMemo(() => {
        if (!virtual) return callerScroll || {x: 1280, y: getGridBodyHeight()};
        const totalWidth = selWidth + scaledColumns.reduce((sum, c) => sum + (c.width || DEFAULT_VIRTUAL_COL_WIDTH), 0);
        const y = callerScroll?.y ?? getGridBodyHeight();
        return {x: totalWidth, y};
    }, [virtual, callerScroll, scaledColumns, selWidth]);
    const mergedTableLayout = virtual ? 'fixed' : callerLayout;

    // Render both but hide one based on screen size
    // Table must stay in DOM to prevent useEffect infinite loop
    // Only show mobile view if mobileCardTitle is provided
    const showMobileView = isMobile && mobileCardTitle;
    
    return (
        <>
            {
                showMobileView && (
                    <MobileCardList
                        name={name}
                        title={title}
                        mobileCardTitle={mobileCardTitle}
                        rowClassName={dataGridProps.rowClassName}
                    />
                )
            }
            <div
                ref={tableWrapRef}
                className={`app-data-grid app-data-grid--${name}`}
                style={{ display: showMobileView ? 'none' : 'block' }}
                // Hücreden çift tıkla seçilen metin baş/sonundaki boşlukla kopyalanıyordu (tarayıcı kelimeye bitişik
                // boşluğu da seçer; değerin kendisi de boşlukla dolu olabilir) → panoya kırpılmış hali yazılır.
                // Input içindeki seçimde getSelection boş döner, tarayıcının kendi kopyalaması çalışır.
                onCopy={(e) => {
                    const text = window.getSelection()?.toString() ?? '';
                    const trimmed = text.trim();
                    if (!trimmed || trimmed === text)
                        return;
                    e.preventDefault();
                    e.clipboardData.setData('text/plain', trimmed);
                }}
                // Hücreye sağ tık değerini panoya kopyalar (RowMenu butonuyla aynı kural): düz sağ tık
                // yalnız bu değeri tutar, Ctrl/⌘ ile önceki kopyalananlara ekler. İstisnalar
                // getCopyableCellText'te; iç içe grid'de içteki işler, dıştaki defaultPrevented görür.
                onContextMenu={(e) => {
                    const text = getCopyableCellText(e);
                    if (text === null)
                        return;
                    e.preventDefault();
                    addText(text, { reset: !(e.ctrlKey || e.metaKey) });
                }}
            >
                <Table
                    size={'small'}
                    scroll={mergedScroll}
                    tableLayout={mergedTableLayout}
                    rowKey={rowKey}
                    columns={scaledColumns}
                    rowSelection={dataGridProps.enableRowSelection ? rowSelection : null}
                    dataSource={resultModel.data}
                    // Yükleme geri bildirimi tablonun İÇİNDE: sayfanın geri kalanı (filtre paneli,
                    // menü, seçim) kullanılabilir kalsın diye. Bu durumu yalnız veriyi çeken bileşen
                    // setLoading ile yönetir; yönetmeyen grid'lerde false kalır ve davranış değişmez.
                    loading={loading ? TABLE_LOADING : false}
                    onChange={onChange}
                    showSorterTooltip={false}
                    expandable={virtual ? undefined : {
                        expandedRowRender: dataGridProps.expandedRowRender,
                        rowExpandable: dataGridProps.rowExpandable,
                    }}
                    rowClassName={dataGridProps.rowClassName}
                    onRow={dataGridProps.onRow}
                    id={tableId}
                    title={() => {
                        return (
                            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                <Button
                                    type={'text'}
                                    size={'small'}
                                    onClick={() => {
                                        openModal(t('Hide/Show Columns'), (
                                            <ColumnFilter
                                                // Liste, kullanıcının kendi kolon sırasıyla gösterilir.
                                                columns={sortColumnsByOrder(dataGridProps.columns, resolveColumnOrder(dataGridProps.columns, readColumnOrder(name)))}
                                                selectedColumnKeys={columns.map(x => x.key)}
                                                onSelectedColumns={(cols) => {
                                                    setColumns(sortColumnsByOrder(cols, resolveColumnOrder(dataGridProps.columns, readColumnOrder(name))))
                                                    localStorage.setItem(`data-grid-${name}`, JSON.stringify(cols))
                                                    closeModal()
                                                }}
                                                onResetOrder={() => {
                                                    writeColumnOrder(name, null)
                                                    setColumns(cols => sortColumnsByOrder(cols, resolveColumnOrder(dataGridProps.columns, null)))
                                                    closeModal()
                                                }}
                                            />
                                        ))
                                    }}
                                >
                                    <UnorderedListOutlined />
                                </Button>
                                <Button
                                    size={'small'}
                                    // Yenile sırasında butonun kendisi döner: kullanıcı isteğinin
                                    // alındığını tam ekran karartma olmadan görür.
                                    loading={loading}
                                    // notifyParent: false → iç içe grid'de (study satırı açıldığında
                                    // gelen istem/ses/dosya grid'leri) yalnız KENDİ tablosunu tazeler.
                                    // Veri değişmediği için dıştaki study grid'ini yenilemenin anlamı yok.
                                    onClick={() => refreshDataGrid({notifyParent: false})}
                                >
                                    <ReloadOutlined />
                                </Button>
                                {title && title()}
                            </div>
                        )
                    }}
                    pagination={{
                        total: gridTotal,
                        pageSize: resultModel.pagination?.pageSize || 10,
                        size: 'small',
                        current: resultModel.pagination?.pageNumber || 1,
                        showTotal: renderTotal,
                        showSizeChanger: true,
                        hideOnSinglePage: false,
                        pageSizeOptions: PAGE_SIZE_OPTIONS
                    }}
                    {...restAntdProps}
                    {...(virtual ? {components: virtualComponents} : {})}
                />
            </div>
        </>
    )
}
